/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { ColumnType } from 'antd/es/table';

import { Node, NodeConfig, NodeConfigFilters, PaginatedNodeConfigsResponse } from '@/client';
import MimicHighlighter from '@/components/MimicHighlighter';
import { PaginatedTable } from '@/components/PaginatedTable';
import { RelativeTime } from '@/components/RelativeTime';
import { TagsList } from '@/components/TagsList';
import { MimicTranslationFunction, useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ConfigRevisionInfo } from '@/pages/ConfigureNodesJobWizard/ConfigureNodesJobWizardSelectConfigRevision';
import { Card } from '@/primitives/Card';
import { Flex } from '@/primitives/Flex';
import { OsIcon } from '@/primitives/Icons';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { tokens } from '@/theme';
import { UpdateParams } from '@/utils/params';

import { ExpandedConfigRowRender } from './ExpandedConfigRowRender';

export type ConfigureNodesJobWizardSelectConfigRevisionMainProps = {
  selectedNodes: Pick<Node, 'id' | 'operatingSystem'>[];
  nodeConfigsData: PaginatedNodeConfigsResponse;
  updateQueryParams: UpdateParams<NodeConfig> | undefined;
  selectedConfigRevisionInfo: ConfigRevisionInfo;
  setSelectedConfigRevisionInfo: (configRevisionInfo: ConfigRevisionInfo) => void;
  setShowConfigCode: (showConfigCode: boolean) => void;
  setError: (error: boolean) => void;
};

export function ConfigureNodesJobWizardSelectConfigRevisionMain({
  selectedNodes,
  nodeConfigsData,
  updateQueryParams,
  selectedConfigRevisionInfo,
  setSelectedConfigRevisionInfo,
  setShowConfigCode,
  setError,
}: ConfigureNodesJobWizardSelectConfigRevisionMainProps) {
  const { t } = useMimicTranslation('configureNodesJobWizard');

  const expandedRowRender = (record: NodeConfig) => (
    <ExpandedConfigRowRender
      nodeConfig={record}
      selectedConfigRevisionInfo={selectedConfigRevisionInfo}
      setSelectedConfigRevisionInfo={setSelectedConfigRevisionInfo}
      setError={setError}
      setShowConfigCode={setShowConfigCode}
    />
  );

  const { filters } = nodeConfigsData.meta;
  const columns = getColumns(t, filters);

  return (
    <div style={{ width: '100%' }}>
      <Card padding="sm" style={{ width: '100%', borderBottom: 'none' }} gap="xs">
        <Title level={5}>{t('selectConfig')}</Title>
        <Flex gap="xs" align="center">
          <OsIcon name={selectedNodes[0].operatingSystem} color={tokens.color.text.textSubtle} />
          <Text type="subtle" size="md">
            {selectedNodes[0].operatingSystem.slice(0, 1).toUpperCase() + selectedNodes[0].operatingSystem.slice(1)}
          </Text>
        </Flex>
      </Card>
      <PaginatedTable
        rowKey={(record) => record.id}
        columns={columns}
        data={nodeConfigsData}
        data-testid="config-table"
        onParamsChange={updateQueryParams}
        expandable={{
          expandedRowRender,
          rowExpandable: () => true,
          expandRowByClick: true,
          defaultExpandedRowKeys: selectedConfigRevisionInfo.configId
            ? [selectedConfigRevisionInfo.configId]
            : undefined,
        }}
      />
    </div>
  );
}

function getColumns(
  t: MimicTranslationFunction<'configureNodesJobWizard'>,
  filters?: NodeConfigFilters,
): ColumnType<NodeConfig>[] {
  const columns: ColumnType<NodeConfig>[] = [
    {
      title: <span data-testid="config-name">{t('configName')}</span>,
      dataIndex: 'name',
      sorter: true,
      filterMode: 'menu',
      render: (_, config) => {
        return <MimicHighlighter searchText={filters?.name} text={config.name} />;
      },
    },
    {
      title: <span data-testid="tags">Tags</span>,
      dataIndex: 'tags',
      width: '200px',
      render: (tags: string[]) => {
        return <TagsList tags={tags} />;
      },
    },
    {
      title: <span data-testid="assigned-to">{t('assignedTo')}</span>,
      dataIndex: 'useCount',
      sorter: true,
      render: (_, config: NodeConfig) => {
        return <Text type="default">{`${config.useCount} ${t('nodesLower')}`}</Text>;
      },
    },
    {
      title: <span data-testid="last-revised">{t('lastRevised')}</span>,
      dataIndex: 'dateUpdated',
      sorter: true,
      render: (_, config: NodeConfig) => <RelativeTime date={config.dateUpdated} />,
      width: '172px',
    },
  ];
  return columns;
}
